import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, File, CheckCircle, Trash2, Download, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DocumentoAlumno {
  id: number;
  tipo: string;
  nombreArchivo: string;
  archivoUrl: string;
  tamanoBytes: number;
  descripcion?: string;
  createdAt: string;
}

export default function AlumnoDocuments() {
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [documentos, setDocumentos] = useState<DocumentoAlumno[]>([]);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await fetch('/api/alumnos/me/documentos', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar documentos');
      }

      const data = await response.json();
      setDocumentos(data.documentos || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, tipo: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo no debe superar los 10MB');
      return;
    }

    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de archivo no permitido. Use PDF, imágenes, Word o Excel');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('documento', file);
    formData.append('tipo', tipo);

    try {
      const response = await fetch('/api/alumnos/me/documentos', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al subir documento');
      }

      const data = await response.json();
      setSuccess(data.message || 'Documento subido correctamente');
      setTimeout(() => setSuccess(''), 3000);
      
      await fetchDocuments();
      
      e.target.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este documento?')) return;

    try {
      const response = await fetch(`/api/alumnos/me/documentos/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al eliminar documento');
      }

      setSuccess('Documento eliminado correctamente');
      setTimeout(() => setSuccess(''), 3000);
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar documento');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getTipoLabel = (tipo: string): string => {
    const labels: { [key: string]: string } = {
      'acta_nacimiento': 'Acta de Nacimiento',
      'certificado_estudios': 'Certificado de Estudios',
      'curp': 'CURP',
      'identificacion_oficial': 'INE/IFE',
      'comprobante_domicilio': 'Comprobante de Domicilio',
    };
    return labels[tipo] || tipo;
  };

  const documentTypes = [
    { id: 'acta_nacimiento', label: 'Acta de Nacimiento', description: 'Copia certificada' },
    { id: 'certificado_estudios', label: 'Certificado de Estudios', description: 'Último nivel cursado' },
    { id: 'curp', label: 'CURP', description: 'Clave Única de Registro de Población' },
    { id: 'identificacion_oficial', label: 'INE/IFE', description: 'Identificación oficial' },
    { id: 'comprobante_domicilio', label: 'Comprobante de Domicilio', description: 'No mayor a 3 meses' },
  ];

  const getDocumentsByType = (tipo: string) => {
    return documentos.filter(doc => doc.tipo === tipo);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Mis Documentos</h1>
        <p className="text-gray-600 mt-2">Sube los documentos requeridos para tu expediente</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 text-green-800 border-green-200">
          <CheckCircle className="w-4 h-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Documentos Requeridos</CardTitle>
          <CardDescription>
            Por favor sube los siguientes documentos en formato PDF o imagen (JPG, PNG)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {documentTypes.map((doc) => {
              const existingDocs = getDocumentsByType(doc.id);
              
              return (
                <div key={doc.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center flex-1">
                      <File className="w-8 h-8 text-blue-600 mr-4" />
                      <div>
                        <h3 className="font-medium">{doc.label}</h3>
                        <p className="text-sm text-gray-600">{doc.description}</p>
                      </div>
                    </div>
                    <div>
                      <input
                        type="file"
                        id={`upload-${doc.id}`}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                        onChange={(e) => handleFileUpload(e, doc.id)}
                        disabled={uploading}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById(`upload-${doc.id}`)?.click()}
                        disabled={uploading}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Subiendo...' : 'Subir'}
                      </Button>
                    </div>
                  </div>

                  {existingDocs.length > 0 && (
                    <div className="mt-3 space-y-2 pl-12">
                      {existingDocs.map((documento) => (
                        <div
                          key={documento.id}
                          className="flex items-center justify-between bg-gray-50 p-3 rounded-md"
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <FileText className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {documento.nombreArchivo}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(documento.tamanoBytes)} • {new Date(documento.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <a
                              href={documento.archivoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button variant="ghost" size="sm">
                                <Download className="w-4 h-4" />
                              </Button>
                            </a>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(documento.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Instrucciones</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-600">
          <p>• Los archivos deben estar en formato PDF, JPG, PNG, Word o Excel</p>
          <p>• El tamaño máximo por archivo es de 10 MB</p>
          <p>• Asegúrate de que los documentos sean legibles</p>
          <p>• Los documentos serán revisados por el área administrativa</p>
          <p>• Puedes subir múltiples versiones del mismo documento</p>
        </CardContent>
      </Card>
    </div>
  );
}
